import { fetchReviewSummary } from '@theme/review-api';

/**
 * Custom element that displays review summary (stars + count)
 */
class ReviewSummary extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    const productId = this.dataset.productId;
    if (productId && productId !== 'PREVIEW-ID') {
      this.loadReviewSummary(productId);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * Loads and renders the review summary
   * @param {string} productId
   */
  async loadReviewSummary(productId) {
    if (!productId || productId === 'PREVIEW-ID') return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const loadingIndicator = this.querySelector('[ref="loadingIndicator"]');
    const ratingContainer = this.querySelector('[ref="ratingContainer"]');
    const countContainer = this.querySelector('[ref="countContainer"]');

    if (loadingIndicator) {
      loadingIndicator.classList.remove('visually-hidden');
    }
    if (ratingContainer) {
      ratingContainer.classList.add('visually-hidden');
    }
    if (countContainer) {
      countContainer.classList.add('visually-hidden');
    }

    try {
      const summary = await fetchReviewSummary(productId);

      if (this.#abortController.signal.aborted) return;

      if (loadingIndicator) {
        loadingIndicator.classList.add('visually-hidden');
      }

      if (!summary || summary.totalReviews === 0) {
        this.classList.add('visually-hidden');
        return;
      }

      this.classList.remove('visually-hidden');
      this.renderReviewSummary(summary);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading review summary:', error);
      }
      if (loadingIndicator) {
        loadingIndicator.classList.add('visually-hidden');
      }
    }
  }

  /**
   * Renders the review summary
   * @param {Object} summary
   * @param {number} summary.averageRating
   * @param {number} summary.totalReviews
   */
  renderReviewSummary(summary) {
    const { averageRating, totalReviews } = summary;
    const blockId = this.closest('[data-block-id]')?.dataset.blockId || 'default';
    const starsStyle = this.dataset.starsStyle || 'shaded';
    const showNumber = this.dataset.showNumber !== 'false';

    // Calculate star display
    const starCount = 5;
    const decimal = averageRating % 1;
    let decimalRating = 0;

    if (decimal >= 0.3 && decimal <= 0.7) {
      decimalRating = 0.5;
    } else if (decimal > 0.7) {
      decimalRating = 1;
    }

    const svgFilledStars = Math.floor(averageRating) + Math.ceil(decimalRating);
    const starSymbolId = `star-summary-${blockId}`;
    const halfGradientId = `half-summary-${blockId}`;

    // Render stars
    let starsHTML = '';
    for (let i = 1; i <= starCount; i++) {
      const isFilled = i <= svgFilledStars;
      const isHalfStar = decimalRating === 0.5 && i === svgFilledStars;
      const emptyFillColor = starsStyle === 'outline' ? 'transparent' : 'rgb(var(--star-fill-color-rgb) / var(--opacity-20))';

      starsHTML += `
        <svg
          class="stars ${isFilled ? 'filled-star' : ''}"
          viewBox="0 0 32 32"
          style="--empty-star-fill-color: ${emptyFillColor}"
          role="presentation"
        >
          <use href="#${starSymbolId}"${isHalfStar ? ` fill="url(#${halfGradientId})"` : ''}></use>
          ${starsStyle === 'outline' ? `<use href="#${starSymbolId}" fill="none" stroke="var(--star-fill-color)" stroke-width="var(--icon-stroke-width)"></use>` : ''}
        </svg>
      `;
    }

    const ratingContainer = this.querySelector('[ref="ratingContainer"]');
    if (ratingContainer) {
      ratingContainer.innerHTML = starsHTML;
      ratingContainer.classList.remove('visually-hidden');
      ratingContainer.setAttribute('aria-label', `Rating: ${averageRating.toFixed(1)} out of 5`);
    }

    // Render count
    const countContainer = this.querySelector('[ref="countContainer"]');
    if (showNumber && countContainer) {
      countContainer.textContent = `${totalReviews} ${totalReviews === 1 ? 'review' : 'reviews'}`;
      countContainer.classList.remove('visually-hidden');
    }
  }
}

if (!customElements.get('review-summary')) {
  customElements.define('review-summary', ReviewSummary);
}
